// ============================================
// OFFICEFORGED — Table Card NodeView
// Renders an editable spreadsheet grid inside ProseMirror
// Each table is a named card in the card engine
// ============================================

export class TableCardView {
  constructor(node, view, getPos, cardEngine) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.cardEngine = cardEngine;

    const rows = node.attrs.rows || 4;
    const cols = node.attrs.cols || 4;
    const cardId = node.attrs.cardId;

    // Build DOM
    this.dom = document.createElement('div');
    this.dom.className = 'table-card-wrapper';
    this.dom.contentEditable = 'false';

    // Header bar with card name
    const header = document.createElement('div');
    header.className = 'table-card-header';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'table-card-name';
    nameSpan.textContent = node.attrs.name || 'Table';
    nameSpan.addEventListener('click', () => this._promptRename(nameSpan));
    const sizeSpan = document.createElement('span');
    sizeSpan.textContent = `${rows}×${cols}`;
    header.appendChild(nameSpan);
    header.appendChild(sizeSpan);
    this.dom.appendChild(header);

    // Build table grid
    const table = document.createElement('table');
    table.className = 'table-card-grid';

    // Column headers (A, B, C, ...)
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const cornerTh = document.createElement('th');
    cornerTh.textContent = '';
    cornerTh.style.width = '36px';
    headerRow.appendChild(cornerTh);
    for (let c = 0; c < cols; c++) {
      const th = document.createElement('th');
      th.textContent = String.fromCharCode(65 + c);
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Data rows
    const tbody = document.createElement('tbody');
    const card = this.cardEngine.getCard(cardId);
    const cellData = (card && card.data.cells) || {};

    for (let r = 0; r < rows; r++) {
      const tr = document.createElement('tr');
      const rowHeader = document.createElement('td');
      rowHeader.className = 'row-header';
      rowHeader.textContent = String(r + 1);
      tr.appendChild(rowHeader);

      for (let c = 0; c < cols; c++) {
        const td = document.createElement('td');
        td.contentEditable = 'true';
        td.dataset.row = r;
        td.dataset.col = c;
        td.textContent = (cellData[r] && cellData[r][c]) || '';

        td.addEventListener('input', () => this._onCellEdit(r, c, td.textContent));
        td.addEventListener('focus', () => this._onCellFocus(r, c));
        td.addEventListener('keydown', (e) => this._onCellKeydown(e, r, c, td));
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    this.dom.appendChild(table);
    this.table = table;

    // Click to select
    this.dom.addEventListener('mousedown', (e) => {
      if (e.target.tagName === 'TD' && e.target.contentEditable === 'true') {
        e.stopPropagation();
      }
    });
  }

  _onCellEdit(row, col, value) {
    const cardId = this.node.attrs.cardId;
    const card = this.cardEngine.getCard(cardId);
    if (!card) return;

    if (!card.data.cells) card.data.cells = {};
    if (!card.data.cells[row]) card.data.cells[row] = {};
    card.data.cells[row][col] = value;
    card.modified = Date.now();

    // Emit update so references re-resolve
    this.cardEngine._emit('card:updated', card);
  }

  _onCellFocus(row, col) {
    this.dom.classList.add('selected');
  }

  _onCellKeydown(e, row, col, td) {
    const rows = this.node.attrs.rows;
    const cols = this.node.attrs.cols;

    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = (col + 1) % cols;
      const nextRow = nextCol === 0 ? row + 1 : row;
      if (nextRow < rows) {
        const nextTd = this.table.querySelector(`td[data-row="${nextRow}"][data-col="${nextCol}"]`);
        if (nextTd) nextTd.focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const nextRow = row + 1;
      if (nextRow < rows) {
        const nextTd = this.table.querySelector(`td[data-row="${nextRow}"][data-col="${col}"]`);
        if (nextTd) nextTd.focus();
      }
    }
  }

  _promptRename(nameSpan) {
    const cardId = this.node.attrs.cardId;
    const current = nameSpan.textContent;
    const newName = prompt('Rename card:', current);
    if (newName && newName !== current) {
      const result = this.cardEngine.renameCard(cardId, newName);
      if (result) {
        nameSpan.textContent = newName;
        // Update PM node attrs
        const pos = this.getPos();
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {
          ...this.node.attrs,
          name: newName
        });
        this.view.dispatch(tr);
      }
    }
  }

  // Required by ProseMirror NodeView API
  stopEvent(event) {
    return true; // Handle all events inside the table ourselves
  }

  ignoreMutation() {
    return true; // We manage our own DOM
  }

  update(node) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    return true;
  }

  destroy() {
    this.dom.classList.remove('selected');
  }
}
